import type { AccountingOwed, AccountingTargets, OverheadGroups } from "@dentacore/shared-types";
import {
  collectionRate,
  meetsTarget,
  overheadRate,
  owedOver60Cents,
  owedOver60Share,
} from "@dentacore/shared-types";

import type { PeriodTotals } from "./figures";
import { money, niceDayShort, percentOne, plural } from "./format";
import styles from "./accounting.module.css";

/**
 * The new top of Yearly and Monthly, and the cards under it (STORY-182b,
 * AF-D7, AF-D8): Collected, Billed, Collection rate, Still owed, Overhead —
 * the figures the research puts first — and where the money goes and what is
 * owed by age.
 *
 * **Nothing here works a figure out.** The rates and shares are the pure rules
 * in `@dentacore/shared-types/accounting`, applied to the API's amounts, so the
 * screen and the server cannot disagree about what a figure is.
 */

/** The four overhead groups, in the order they stack and are listed. */
export const OVERHEAD_GROUPS = [
  { key: "staff", label: "Staff", className: styles.ohStaff, swatch: styles.swatchStaff },
  {
    key: "suppliesLab",
    label: "Supplies and lab",
    className: styles.ohSupplies,
    swatch: styles.swatchSupplies,
  },
  {
    key: "occupancy",
    label: "Occupancy",
    className: styles.ohOccupancy,
    swatch: styles.swatchOccupancy,
  },
  { key: "other", label: "Other", className: styles.ohOther, swatch: styles.swatchOther },
] as const satisfies readonly {
  key: keyof OverheadGroups;
  label: string;
  className: string | undefined;
  swatch: string | undefined;
}[];

/**
 * How a tile stands against its target (AF-D6): coloured, **and said in words**
 * on the line under it, so colour is never the only signal. Nothing at all when
 * no target is set or there is nothing to judge — a clinic that set none sees
 * no red or green.
 */
function against(
  met: boolean | null,
  basisPoints: number | null,
  direction: "≥" | "≤",
): { className: string | undefined; text: string | null } {
  if (met === null || basisPoints === null) return { className: undefined, text: null };
  return {
    className: met ? styles.profit : styles.loss,
    text: `Target ${direction} ${percentOne(basisPoints / 10_000)} — ${met ? "met" : "missed"}`,
  };
}

/**
 * The five tiles. A tile reads "—" rather than 0% when there is nothing to
 * divide by, and a rate is coloured against the clinic's target only when one is
 * set (AF-D6).
 */
export function HeadlineTiles({
  totals,
  owed,
  started,
  currency,
  targets,
}: {
  totals: PeriodTotals;
  owed: AccountingOwed;
  /** False for a period that has not begun: no cash can have moved in it. */
  started: boolean;
  currency: string;
  /** Null until they have been read; nothing is judged meanwhile. */
  targets: AccountingTargets | null;
}): React.JSX.Element {
  const moneyOut = totals.materials + totals.salaries + totals.expenses;
  const rate = collectionRate(totals.moneyIn, totals.billed);
  const overhead = overheadRate(moneyOut, totals.moneyIn);
  const old = owedOver60Share(owed.buckets);

  const rateStanding = against(
    meetsTarget("collectionRate", totals.moneyIn, totals.billed, targets?.collectionRate ?? null),
    targets?.collectionRate ?? null,
    "≥",
  );
  const owedStanding = against(
    meetsTarget(
      "owedOver60",
      owedOver60Cents(owed.buckets),
      owed.totalCents,
      targets?.owedOver60 ?? null,
    ),
    targets?.owedOver60 ?? null,
    "≤",
  );
  const overheadStanding = against(
    meetsTarget("overhead", moneyOut, totals.moneyIn, targets?.overhead ?? null),
    targets?.overhead ?? null,
    "≤",
  );

  return (
    <div className={styles.facts} data-testid="accounting-headline">
      <div className={styles.fact}>
        <div className={styles.factKey}>Collected</div>
        <div className={styles.factValue} data-testid="accounting-collected">
          {started ? money(totals.moneyIn, currency) : "—"}
        </div>
        <div className={styles.factSub}>{started ? "Payments received" : "Not started"}</div>
      </div>
      <div className={styles.fact}>
        <div className={styles.factKey}>Billed</div>
        <div className={styles.factValue} data-testid="accounting-billed">
          {money(totals.billed, currency)}
        </div>
        <div className={styles.factSub}>{plural(totals.billedCount, "invoice")} issued</div>
      </div>
      <div className={styles.fact}>
        <div className={styles.factKey}>Collection rate</div>
        <div
          className={`${styles.factValue} ${rateStanding.className ?? ""}`}
          data-testid="accounting-rate"
        >
          {rate === null ? "—" : percentOne(rate)}
        </div>
        <div className={styles.factSub}>
          {rate === null ? "Nothing billed" : "Collected ÷ billed, in this period"}
        </div>
        {rateStanding.text !== null && (
          <div className={styles.factSub} data-testid="accounting-rate-target">
            {rateStanding.text}
          </div>
        )}
      </div>
      <div className={styles.fact}>
        <div className={styles.factKey}>Still owed</div>
        <div className={styles.factValue} data-testid="accounting-owed">
          {money(owed.totalCents, currency)}
        </div>
        <div className={styles.factSub}>
          {owed.invoiceCount === 0
            ? `Nothing, as of ${niceDayShort(owed.asOf)}`
            : `${plural(owed.invoiceCount, "invoice")}${old === null ? "" : `, ${percentOne(old)} over 60 days`}`}
        </div>
        {owedStanding.text !== null && (
          <div
            className={`${styles.factSub} ${owedStanding.className ?? ""}`}
            data-testid="accounting-owed-target"
          >
            {owedStanding.text}
          </div>
        )}
      </div>
      <div className={styles.fact}>
        <div className={styles.factKey}>Overhead</div>
        <div
          className={`${styles.factValue} ${overheadStanding.className ?? ""}`}
          data-testid="accounting-overhead"
        >
          {overhead === null ? "—" : percentOne(overhead)}
        </div>
        <div className={styles.factSub}>
          {overhead === null ? "Nothing came in" : `${money(moneyOut, currency)} out of money in`}
        </div>
        {overheadStanding.text !== null && (
          <div className={styles.factSub} data-testid="accounting-overhead-target">
            {overheadStanding.text}
          </div>
        )}
      </div>
    </div>
  );
}

/** Billed behind, collected in front — named, so neither is colour alone. */
export function BilledLegend(): React.JSX.Element {
  return (
    <div className={styles.legend} aria-hidden="true">
      <span>
        <i className={`${styles.swatch} ${styles.swatchGhost}`} />
        Billed
      </span>
      <span>
        <i className={`${styles.swatch} ${styles.swatchProfit}`} />
        Collected
      </span>
    </div>
  );
}

/** The four groups' colours, as a legend. */
export function OverheadLegend(): React.JSX.Element {
  return (
    <div className={styles.legend} aria-hidden="true">
      {OVERHEAD_GROUPS.map((group) => (
        <span key={group.key}>
          <i className={`${styles.swatch} ${group.swatch ?? ""}`} />
          {group.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Where a period's money goes: each group's amount and its share of money in.
 * The four add up to money out (AF-D4), and the line under them says so.
 */
export function OverheadRows({
  groups,
  moneyIn,
  currency,
}: {
  groups: OverheadGroups;
  moneyIn: number;
  currency: string;
}): React.JSX.Element {
  const total = OVERHEAD_GROUPS.reduce((sum, group) => sum + groups[group.key], 0);
  const top = Math.max(1, ...OVERHEAD_GROUPS.map((group) => groups[group.key]));
  return (
    <div className={styles.procBars} data-testid="accounting-overhead-rows">
      {OVERHEAD_GROUPS.map((group) => {
        const cents = groups[group.key];
        const share = overheadRate(cents, moneyIn);
        return (
          <div key={group.key} className={styles.procRow}>
            <span className={styles.lineLabel}>{group.label}</span>
            <span className={styles.procTrack} aria-hidden="true">
              <i
                className={`${styles.ohFill} ${group.swatch ?? ""}`}
                style={{ width: `${String(Math.max(0, (cents / top) * 100))}%` }}
              />
            </span>
            <span className={styles.num}>
              {money(cents, currency)}
              <span className={styles.procOf}>
                {" "}
                {share === null ? "" : `· ${percentOne(share)}`}
              </span>
            </span>
          </div>
        );
      })}
      <p className={styles.note}>
        Money out, {money(total, currency)}
        {moneyIn > 0 && ` — ${percentOne(total / moneyIn)} of what came in`}.
      </p>
    </div>
  );
}

/** What is owed, by how long since each invoice was issued — as of today (AF-D3). */
export function OwedByAge({
  owed,
  currency,
}: {
  owed: AccountingOwed;
  currency: string;
}): React.JSX.Element {
  const top = Math.max(1, ...owed.buckets.map((bucket) => bucket.cents));
  const labels: Record<string, string> = {
    "0-30": "0–30 days",
    "31-60": "31–60 days",
    "61-90": "61–90 days",
    "91+": "Over 90 days",
  };
  return (
    <div className={styles.procBars} data-testid="accounting-owed-ages">
      {owed.buckets.map((bucket) => {
        const late = bucket.key === "61-90" || bucket.key === "91+";
        return (
          <div
            key={bucket.key}
            className={styles.procRow}
            title={`${labels[bucket.key] ?? bucket.key}: ${money(bucket.cents, currency)} on ${plural(bucket.count, "invoice")}`}
          >
            <span className={styles.lineLabel}>{labels[bucket.key]}</span>
            <span className={styles.procTrack} aria-hidden="true">
              <i
                className={late ? styles.fillOut : styles.fill}
                style={{ width: `${String(Math.max(0, (bucket.cents / top) * 100))}%` }}
              />
            </span>
            <span className={styles.num}>
              {money(bucket.cents, currency)}
              <span className={styles.procOf}> · {plural(bucket.count, "invoice")}</span>
            </span>
          </div>
        );
      })}
      <p className={styles.note} data-testid="accounting-owed-note">
        As of {niceDayShort(owed.asOf)}, whichever year or month is open. Counted from the day each
        invoice was issued; drafts and withdrawn invoices are not owed.
      </p>
    </div>
  );
}
